import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {UnavailableProductTile} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/delivery.types';
import {useTranslation} from '@aboutyou/i18n';
import {useTrackFeature, useTrackProductClick} from '@aboutyou/tracking';
import {Slide} from '@aboutyou/transitions';
import {
  COLORS,
  GenericIconName,
  ProductCardUnavailable,
  tabletAndUp,
} from '@aboutyou/ui-components';
import React, {useState} from 'react';
import styled, {css} from 'styled-components';
import {
  SizeReminderSizeProps,
  SizeSectionSizeReminder,
} from '../../../features/SizeSelection/SizeSectionSizeReminder';
import {
  AnimatedIcon,
  DeleteItemButton,
} from '../Deliveries/Components/BasketProductCard';
import {DeleteWithUndo} from '../Deliveries/Components/DeleteWithUndo/DeleteWithUndo';
import {WishlistButton} from '../Deliveries/Components/WishlistButton';
import {useBasketDeliveryOptionalRemovedState} from '../Deliveries/hooks/useBasketDeliveryOptionalRemovedState';
import {UnavailableProductsProps} from './UnavailableProducts';

interface UnavailableProductItemProps {
  item: UnavailableProductTile;
}

export const UnavailableProductItem: React.FC<
  UnavailableProductItemProps & UnavailableProductsProps
> = ({item, SizeReminderLayoutComponent}) => {
  const {
    isItemMarkedForDeletion,
    deleteItemWithDelay,
    deleteItem,
    cancelDeletion,
    onDeleteIconClick,
  } = useBasketDeliveryOptionalRemovedState({
    itemKey: item.itemKey,
  });

  const {t} = useTranslation();
  const [sizeReminderSize, setSizeReminderSize] = useState<
    SizeReminderSizeProps | undefined
  >();

  const trackFeature = useTrackFeature();
  const productTileTrackingData = {
    product: item.product,
    brand: item.brand,
    badges: [],
    colors: [],
    highlightType: 0,
    primaryBustImageGallery: [],
    primaryModelImageGallery: [],
  };

  const onRemind = () => {
    cancelDeletion();
    trackFeature({
      eventType: 'click',
      label: `basket_size_reminder|${item.product.id}`,
    });

    setSizeReminderSize({
      sizeId: item.selectedSize.id,
      label: item.sizeLabel ?? t('One-size'),
    } as SizeSelection_Size);
  };

  const handleCancelDeletion = () => {
    cancelDeletion();
  };

  const onSizeReminderClose = () => {
    setSizeReminderSize(undefined);
  };

  const trackProductClick = useTrackProductClick();

  return (
    <>
      <Wrapper>
        <StyledProductCardUnavailable
          product={{
            path: item.product.path,
            image: item.product.primaryBustImage,
            name: item.product.name,
            colorLabel: item.product.colorLabel,
            brand: item.brand.name,
            sizeLabel: item.sizeLabel || '',
          }}
          trackProductClick={() =>
            trackProductClick(productTileTrackingData, 1)
          }
          wishlistButton={
            <WishlistButton brand={item.brand} product={item.product} />
          }
          onClickRemind={onRemind}
          deleteBlock={
            <DeleteItemButton
              onClick={onDeleteIconClick}
              aria-label={t('remove product')}
            >
              <AnimatedIcon
                size="S"
                asset={GenericIconName.Trash}
                fill={COLORS.Grey1}
              />
            </DeleteItemButton>
          }
        />
        <Slide
          mounted={isItemMarkedForDeletion}
          direction="left"
          duration={250}
        >
          <DeleteWithUndo
            itemKey={item.itemKey}
            deleteItem={deleteItem}
            deleteItemWithDelay={deleteItemWithDelay}
            onUndoClick={handleCancelDeletion}
            shouldCheckForPriceRise={false}
          />
        </Slide>
      </Wrapper>
      {sizeReminderSize && (
        <SizeSectionSizeReminder
          brand={item.brand}
          onResetSizeReminderSize={onSizeReminderClose}
          product={item.product}
          sizeReminderSize={sizeReminderSize}
          SizeReminderLayoutComponent={SizeReminderLayoutComponent}
          sizeSelectionOpenedStateKey={undefined}
          isOpen={Boolean(sizeReminderSize)}
        />
      )}
    </>
  );
};

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const StyledProductCardUnavailable = styled(ProductCardUnavailable)`
  padding-inline: 16px;
  ${tabletAndUp(
    css`
      padding-inline: 24px;
    `,
  )}
`;
